// Pure helpers for the places (geofence) editor.
import type { Schemas } from "@10twenty/api";
import { distanceM } from "../live/motion";

type Place = Schemas["Place"];
type PlaceCreate = Schemas["PlaceCreate"];
type PlaceUpdate = Schemas["PlaceUpdate"];
type Member = Schemas["Member"];
type MemberState = Schemas["MemberState"];

export const NAME_MAX = 80;
export const ADDRESS_MAX = 300;
export const RADIUS_MIN_M = 50;
export const RADIUS_MAX_M = 5000;
export const DEFAULT_RADIUS_M = 150;
export const PLACE_LIMIT = 100;
/** Steps of the radius slider, which is logarithmic between the limits. */
export const SLIDER_STEPS = 1000;

/** The editor's working copy of a place; the center is null until picked on the map. */
export interface PlaceDraft {
  name: string;
  address: string;
  lat: number | null;
  lon: number | null;
  radiusM: number;
}

export interface DraftErrors {
  name?: string;
  address?: string;
  center?: string;
}

export function draftOf(place?: Place): PlaceDraft {
  if (!place) return { name: "", address: "", lat: null, lon: null, radiusM: DEFAULT_RADIUS_M };
  return { name: place.name, address: place.address ?? "", lat: place.lat, lon: place.lon, radiusM: place.radius_m };
}

/** Problems that block saving; empty when the draft is valid. */
export function draftErrors(draft: PlaceDraft): DraftErrors {
  const errors: DraftErrors = {};
  const name = draft.name.trim();
  if (!name) errors.name = "Enter a name.";
  else if (name.length > NAME_MAX) errors.name = `Names can be up to ${NAME_MAX} characters.`;
  if (draft.address.trim().length > ADDRESS_MAX) errors.address = `Addresses can be up to ${ADDRESS_MAX} characters.`;
  if (draft.lat === null || draft.lon === null) errors.center = "Click the map where the place is.";
  return errors;
}

const isValid = (draft: PlaceDraft) => Object.keys(draftErrors(draft)).length === 0;

/** The create request for a valid draft, or null. */
export function createBody(draft: PlaceDraft): PlaceCreate | null {
  if (!isValid(draft)) return null;
  const address = draft.address.trim();
  return {
    name: draft.name.trim(),
    lat: draft.lat!,
    lon: draft.lon!,
    radius_m: clampRadius(draft.radiusM),
    ...(address ? { address } : {}),
  };
}

/**
 * Only the fields that changed, or null when nothing did (or the draft is
 * invalid). A blank address is sent as null, which clears it.
 */
export function updateBody(place: Place, draft: PlaceDraft): PlaceUpdate | null {
  if (!isValid(draft)) return null;
  const body: PlaceUpdate = {};
  const name = draft.name.trim();
  const address = draft.address.trim() || null;
  const radius = clampRadius(draft.radiusM);
  if (name !== place.name) body.name = name;
  if (draft.lat !== place.lat) body.lat = draft.lat!;
  if (draft.lon !== place.lon) body.lon = draft.lon!;
  if (radius !== place.radius_m) body.radius_m = radius;
  if (address !== (place.address ?? null)) body.address = address;
  return Object.keys(body).length ? body : null;
}

export const clampRadius = (m: number): number =>
  Number.isFinite(m) ? Math.min(RADIUS_MAX_M, Math.max(RADIUS_MIN_M, m)) : DEFAULT_RADIUS_M;

/** Rounds a radius to a step that suits its size: 10 m up to 200 m, then 25, 50 and 100 m. */
export function snapRadius(m: number): number {
  const step = m < 200 ? 10 : m < 500 ? 25 : m < 1000 ? 50 : 100;
  return clampRadius(Math.round(m / step) * step);
}

const LOG_SPAN = Math.log(RADIUS_MAX_M / RADIUS_MIN_M);

/** Slider position (0–SLIDER_STEPS) to a snapped radius in metres. */
export function sliderToRadius(value: number): number {
  const fraction = Math.min(1, Math.max(0, value / SLIDER_STEPS));
  return snapRadius(RADIUS_MIN_M * Math.exp(fraction * LOG_SPAN));
}

/** Radius in metres to the nearest slider position. */
export function radiusToSlider(m: number): number {
  return Math.round((Math.log(clampRadius(m) / RADIUS_MIN_M) / LOG_SPAN) * SLIDER_STEPS);
}

/** "150 m", "1.2 km", "5 km". */
export function formatRadius(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(Math.round(m / 100) / 10).toLocaleString("en", { maximumFractionDigits: 1 })} km`;
}

/** A map click as a place center: longitude wrapped into [-180, 180], both rounded to about 10 cm. */
export function pickedCenter(lat: number, lon: number): { lat: number; lon: number } {
  const wrapped = ((((lon + 180) % 360) + 360) % 360) - 180;
  const round = (value: number) => Math.round(value * 1e6) / 1e6;
  return {
    lat: round(Math.min(90, Math.max(-90, lat))),
    lon: round(wrapped === -180 && lon > 0 ? 180 : wrapped),
  };
}

/** Area framed around located members when a circle has no places yet (about 500 m around each). */
export function memberFrame(locations: readonly { lat: number; lon: number }[]): [number, number][] {
  return locations.flatMap(({ lat, lon }) => {
    const dLat = 0.0045;
    const dLon = dLat / Math.max(Math.cos((lat * Math.PI) / 180), 1e-6);
    return [
      [lon - dLon, lat - dLat],
      [lon + dLon, lat + dLat],
    ] as [number, number][];
  });
}

/** Places by name (case-insensitive), then id for a stable order. */
export function sortPlaces<T extends Pick<Place, "id" | "name">>(places: readonly T[]): T[] {
  return [...places].sort(
    (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || (a.id < b.id ? -1 : 1),
  );
}

/** Where a new place can start from a member: their current stop, else their latest fix. */
export interface MemberStart {
  userId: string;
  /** "You" for the caller, else the member's name. */
  label: string;
  lat: number;
  lon: number;
  /** A radius covering their stop, or null to keep the draft's. */
  radiusM: number | null;
  /** When the member's latest fix was taken (epoch ms). */
  at: number;
}

/**
 * Members with a precise location (none while paused or sharing an area),
 * the caller first, then by name.
 */
export function memberStarts(
  members: readonly Pick<Member, "user_id" | "name">[],
  states: readonly MemberState[],
  selfId: string | null,
): MemberStart[] {
  const names = new Map(members.map((member) => [member.user_id, member.name]));
  const starts = states.flatMap((state): MemberStart[] => {
    const name = names.get(state.user_id);
    const location = state.location;
    if (name === undefined || !location) return [];
    const stop = state.stop ?? null;
    return [
      {
        userId: state.user_id,
        label: state.user_id === selfId ? "You" : name,
        lat: stop?.lat ?? location.lat,
        lon: stop?.lon ?? location.lon,
        // Twice the stop's spread keeps its fixes well inside.
        radiusM: stop ? Math.max(DEFAULT_RADIUS_M, snapRadius(2 * stop.radius_m)) : null,
        at: Date.parse(location.ts),
      },
    ];
  });
  return starts.sort(
    (a, b) =>
      Number(b.userId === selfId) - Number(a.userId === selfId) ||
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
}

/** The draft change that centres a place on a member's start. */
export function startAt(start: MemberStart): Partial<PlaceDraft> {
  return { ...pickedCenter(start.lat, start.lon), ...(start.radiusM === null ? {} : { radiusM: start.radiusM }) };
}

/** Whether the draft is centred on that start. */
export function isAt(draft: PlaceDraft, start: MemberStart): boolean {
  const center = pickedCenter(start.lat, start.lon);
  return draft.lat === center.lat && draft.lon === center.lon;
}

/** The position the server looks names up for (4 decimal places, about 11 m), so nearby picks share a lookup. */
export function lookupPosition(lat: number, lon: number): { lat: number; lon: number } {
  const round = (value: number) => Math.round(value * 10_000) / 10_000;
  return { lat: round(lat), lon: round(lon) };
}

/** Names offered whatever the geocoder knows. */
export const NAME_PRESETS = ["Home", "Work", "School"] as const;

/**
 * Names to offer for a new place: the presets, then the geocoder's names,
 * leaving out repeats and names the circle's places already have
 * (case-insensitively).
 */
export function nameSuggestions(
  geocoded: readonly string[],
  places: readonly Pick<Place, "name">[],
  limit = 6,
): string[] {
  const taken = new Set(places.map((place) => place.name.trim().toLocaleLowerCase()));
  const names: string[] = [];
  for (const candidate of [...NAME_PRESETS, ...geocoded]) {
    const name = candidate.trim();
    const key = name.toLocaleLowerCase();
    if (!name || name.length > NAME_MAX || taken.has(key)) continue;
    taken.add(key);
    names.push(name);
    if (names.length === limit) break;
  }
  return names;
}

/** Text taken from the geocoder's suggestions, and the center it was looked up for. */
export interface FilledText {
  text: string;
  lat: number;
  lon: number;
}

/** The draft's name and address, where they came from the geocoder. */
export interface Filled {
  name: FilledText | null;
  address: FilledText | null;
}

export const NOTHING_FILLED: Filled = { name: null, address: null };

/** Takes a suggested name or address; geocoded text remembers its center (presets are not geocoded). */
export function fillDraft(
  draft: PlaceDraft,
  filled: Filled,
  patch: { name: string } | { address: string },
): { draft: PlaceDraft; filled: Filled } {
  const next = { ...draft, ...patch };
  const field = "name" in patch ? "name" : "address";
  const text = "name" in patch ? patch.name : patch.address;
  const geocoded =
    draft.lat !== null &&
    draft.lon !== null &&
    !(field === "name" && (NAME_PRESETS as readonly string[]).includes(text));
  return {
    draft: next,
    filled: { ...filled, [field]: geocoded ? { text, lat: draft.lat!, lon: draft.lon! } : null },
  };
}

/**
 * Applies a change of center (and maybe radius). Geocoded text the user has
 * not edited is cleared once the place no longer covers the center it was
 * looked up for: it described another spot.
 */
export function moveDraft(
  draft: PlaceDraft,
  filled: Filled,
  patch: Partial<PlaceDraft>,
): { draft: PlaceDraft; filled: Filled } {
  const next = { ...draft, ...patch };
  const settle = (field: "name" | "address"): FilledText | null => {
    const origin = filled[field];
    // Edited since: it is the user's own text now.
    if (!origin || draft[field] !== origin.text) return null;
    if (next.lat === null || next.lon === null) return origin;
    if (distanceM([origin.lon, origin.lat], [next.lon, next.lat]) <= next.radiusM) return origin;
    next[field] = "";
    return null;
  };
  return { draft: next, filled: { name: settle("name"), address: settle("address") } };
}
